import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { motion, useReducedMotion } from "framer-motion";
import { color, ease, font, shadow, z } from "../tokens";
import {
  DisplayTitle,
  Inner,
  MonoLabel,
  Reveal,
  STATIC_MODE,
  useReveal,
} from "../primitives";
import { SENIOR_SECTION, CardData } from "../data";
import AnimatedIcon from "../icons";
import MissionBoard from "./senior/MissionBoard";
import { ENGAGEMENTS } from "./senior/missionData";
import agenticOrchestrator from "../../images/agentic-orchestrator.webp";
import imogenDemo from "../../images/imogen-chat-demo.webp";
import urbnsurfDemo from "../../images/urbnsurf-portfolio-demo.webp";

/**
 * Section — 05 Palo IT senior era: the dark interlude. The paper site
 * breaks open into a full-bleed mission-control panel where a scroll-
 * scrubbed orchestrator board executes the era ticket by ticket; the
 * completed run then compiles into the five engagement rows below,
 * still inside the machine.
 */

/* ------------------------------------------------------------- the wrap */

/* Sits above the fixed timeline rail (z 39) so the paper-era timeline
   visibly disappears "inside the machine" and re-emerges after. */
const MissionWrap = styled.section`
  position: relative;
  z-index: ${z.nav - 1};
  background: ${color.ink};
  color: ${color.paper};
  padding-top: clamp(72px, 12vh, 150px);
  padding-bottom: clamp(120px, 20vh, 220px);
  margin-bottom: clamp(120px, 22vh, 240px);
`;

const TitleBlock = styled.div`
  margin-bottom: clamp(36px, 7vh, 90px);
`;

const TitleMeta = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 18px;
  flex-wrap: wrap;
`;

const Statement = styled.p`
  margin: clamp(20px, 3.5vh, 36px) 0 0;
  font-family: ${font.body};
  font-size: clamp(18px, 2vw, 25px);
  font-weight: 500;
  line-height: 1.4;
  letter-spacing: -0.01em;
  max-width: 30em;
  color: ${color.paper};
  text-wrap: pretty;
`;

const StatementMuted = styled.span`
  color: ${color.mutedOnInk};
`;

/* ---------------------------------------------------------- compile rows */

const RowsHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: clamp(56px, 10vh, 120px);
  margin-bottom: clamp(18px, 3vh, 32px);
`;

type Phase = 0 | 1 | 2; // AWAITING | COMPILING | SHIPPED

const useCompile = (steps: number, threshold = 0.82) => {
  const { ref, shown } = useReveal(threshold);
  const [phase, setPhase] = useState<Phase>(STATIC_MODE ? 2 : 0);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (STATIC_MODE) return;
    if (!shown) {
      setPhase(0);
      return;
    }
    if (reduced) {
      setPhase(2);
      return;
    }
    setPhase(1);
    const id = window.setTimeout(() => setPhase(2), 600 + steps * 260);
    return () => window.clearTimeout(id);
  }, [shown, steps, reduced]);

  return { ref, phase };
};

const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(150px, 0.9fr) minmax(0, 2.6fr) minmax(
      160px,
      1fr
    );
  gap: clamp(20px, 3vw, 56px);
  padding: clamp(28px, 4.5vh, 48px) 0;
  border-top: 1px solid ${color.lineOnInk};
  transition: background-color 0.3s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover {
    background-color: rgba(244, 240, 232, 0.04);
  }

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    gap: 14px;
  }
`;

const TicketMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
`;

const SerialStack = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const StatusChip = styled(motion.span)<{ $phase: Phase }>`
  font-family: ${font.mono};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid
    ${(p) =>
      p.$phase === 0
        ? "rgba(244, 240, 232, 0.28)"
        : p.$phase === 1
          ? color.accent
          : color.pastel.sage};
  background: ${(p) =>
    p.$phase === 0
      ? "transparent"
      : p.$phase === 1
        ? color.accent
        : color.pastel.sage};
  color: ${(p) => (p.$phase === 0 ? color.mutedOnInk : color.ink)};
  transition:
    background-color 0.3s cubic-bezier(0.16, 1, 0.3, 1),
    border-color 0.3s cubic-bezier(0.16, 1, 0.3, 1),
    color 0.3s cubic-bezier(0.16, 1, 0.3, 1);
`;

const STATUS_LABEL: Record<Phase, string> = {
  0: "Awaiting run",
  1: "Compiling",
  2: "Shipped ✓",
};

/* Provenance — which board tickets this row was compiled from. */
const SrcChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
`;

const SrcChip = styled(motion.span)`
  font-family: ${font.mono};
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.07em;
  color: ${color.mutedOnInk};
  border: 1px solid ${color.lineOnInk};
  border-radius: 6px;
  padding: 3px 7px;
  white-space: nowrap;
`;

const SrcCheck = styled.span`
  color: ${color.pastel.sage};
  font-weight: 700;
  margin-left: 4px;
`;

const RowTitle = styled(motion.h3)`
  margin: 0 0 4px;
  font-family: ${font.display};
  font-size: clamp(26px, 3.2vw, 44px);
  font-weight: 400;
  line-height: 1.02;
  letter-spacing: 0.01em;
  text-transform: uppercase;
  transform-origin: left bottom;
  color: ${color.paper};
`;

const RowClient = styled.p`
  margin: 0 0 14px;
  font-family: ${font.mono};
  font-size: 11.5px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${color.mutedOnInk};
`;

const RowBullets = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-family: ${font.body};
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
  max-width: 62ch;
  text-wrap: pretty;
`;

const Bullet = styled(motion.li)<{ $primary?: boolean }>`
  display: flex;
  gap: 10px;
  align-items: baseline;
  color: ${(p) => (p.$primary ? color.paper : color.mutedOnInk)};
`;

const Check = styled(motion.span)`
  font-family: ${font.mono};
  font-size: 12px;
  font-weight: 700;
  color: ${color.accent};
  flex: none;
`;

const RowMedia = styled(motion.div)`
  margin-top: 18px;
  max-width: 520px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: ${shadow.card};
  background: rgba(255, 255, 255, 0.92);
  padding: clamp(12px, 1.5vw, 20px);

  img {
    display: block;
    width: 100%;
    border-radius: 6px;
  }
`;

const ChipStack = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;

  @media (max-width: 860px) {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: flex-start;
  }
`;

const Chip = styled.span<{ $bg: string }>`
  font-family: ${font.mono};
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${color.ink};
  background: ${(p) => p.$bg};
  padding: 5px 10px;
  border-radius: 999px;
  white-space: nowrap;
`;

/* ------------------------------------------------- Vista orchestrator */

const Artifact = styled.div`
  margin: clamp(20px, 3vh, 36px) 0 clamp(8px, 2vh, 20px);
  background: ${color.pastel.lavender};
  border-radius: 18px;
  padding: clamp(24px, 3.5vw, 44px);
  box-shadow: ${shadow.card};
  color: ${color.ink};
`;

const ArtifactHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: clamp(24px, 4vh, 40px);
`;

const Diagram = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: clamp(16px, 3vw, 48px);
  align-items: center;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    justify-items: center;
  }
`;

const NodeColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  &:first-child {
    align-items: flex-end;
  }
  &:last-child {
    align-items: flex-start;
  }

  @media (max-width: 860px) {
    align-items: center !important;
  }
`;

const NodeChip = styled(motion.span)`
  font-family: ${font.mono};
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${color.ink};
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid transparent;
  padding: 8px 14px;
  border-radius: 999px;
  transition:
    border-color 0.25s cubic-bezier(0.16, 1, 0.3, 1),
    background-color 0.25s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover {
    border-color: ${color.ink};
    background: ${color.accent};
  }
`;

const Hub = styled(motion.div)`
  font-family: ${font.display};
  font-size: clamp(17px, 1.7vw, 23px);
  font-weight: 400;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  text-align: center;
  background: ${color.ink};
  color: ${color.paper};
  border-radius: 14px;
  padding: 18px 26px;
  line-height: 1.15;
`;

const HubSub = styled.span`
  display: block;
  margin-top: 6px;
  font-family: ${font.mono};
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.08em;
  color: ${color.mutedOnInk};
`;

const ArtifactShot = styled(motion.div)`
  margin-top: clamp(20px, 3vh, 32px);
  border-radius: 10px;
  overflow: hidden;

  img {
    display: block;
    width: 100%;
  }
`;

const INTEGRATIONS = ["GitHub", "Azure", "Cursor", "Copilot", "OpenRouter"];
const OUTPUTS = [
  "Tickets completed autonomously",
  "WinForms → Angular / C#",
  "1980–2026 code, one pipeline",
];

const ROW_MEDIA: Record<string, { src: string; alt: string }> = {
  imed: { src: imogenDemo, alt: "IMOGEN chatbot demo" },
  urbnsurf: { src: urbnsurfDemo, alt: "UrbnSurf booking flow demo" },
};

/* Row provenance: the board tickets each engagement compiled from. */
const SOURCE_KEYS: Record<string, string[]> = {};
ENGAGEMENTS.forEach((eng) => {
  SOURCE_KEYS[eng.card.project.id] = [1, 2, 3, 4].map(
    (n) => `${eng.tag}-${String(n).padStart(2, "0")}`,
  );
});

/* ------------------------------------------------------- a compiled row */

const pop = { duration: 0.45, ease: ease.out };

const CompiledRow: React.FC<{
  card: CardData;
  renderArtifact?: (phase: Phase) => React.ReactNode;
}> = ({ card, renderArtifact }) => {
  const media = ROW_MEDIA[card.project.id];
  const bulletCount = card.project.bullets.length;
  const { ref, phase } = useCompile(bulletCount);
  const running = phase >= 1;
  const done = phase === 2;
  const sources = SOURCE_KEYS[card.project.id] ?? [];

  return (
    <Row ref={ref as React.RefObject<HTMLDivElement>}>
      <TicketMeta>
        <SerialStack>
          <AnimatedIcon
            name={card.project.icon}
            size={15}
            strokeWidth={2}
            color={color.paper}
            active={running}
          />
          <MonoLabel $onInk>{card.serial}</MonoLabel>
        </SerialStack>
        <StatusChip
          $phase={phase}
          animate={phase === 1 ? { opacity: [1, 0.55, 1] } : { opacity: 1 }}
          transition={
            phase === 1
              ? { duration: 1.1, repeat: Infinity, ease: "easeInOut" }
              : { duration: 0.2 }
          }
        >
          {STATUS_LABEL[phase]}
        </StatusChip>
        <SrcChips aria-hidden>
          {sources.map((key, i) => (
            <SrcChip
              key={key}
              initial={false}
              animate={running ? { opacity: 1, y: 0 } : { opacity: 0, y: 6 }}
              transition={{ ...pop, delay: running ? 0.1 + i * 0.1 : 0 }}
            >
              {key}
              <SrcCheck>✓</SrcCheck>
            </SrcChip>
          ))}
        </SrcChips>
      </TicketMeta>
      <div>
        <RowTitle
          initial={false}
          animate={
            running
              ? { opacity: 1, scale: 1, y: 0 }
              : { opacity: 0, scale: 0.94, y: 12 }
          }
          transition={pop}
        >
          {card.project.title}
        </RowTitle>
        <RowClient>{card.project.client}</RowClient>
        <RowBullets>
          {card.project.bullets.map((b, i) => (
            <Bullet
              key={b}
              $primary={i === 0}
              initial={false}
              animate={running ? { opacity: 1, x: 0 } : { opacity: 0, x: -14 }}
              transition={{ ...pop, delay: running ? 0.2 + i * 0.24 : 0 }}
            >
              <Check
                initial={false}
                animate={{ opacity: running ? 1 : 0 }}
                transition={{
                  duration: 0.2,
                  delay: running ? 0.42 + i * 0.24 : 0,
                }}
                aria-hidden
              >
                ✓
              </Check>
              <span>{b}</span>
            </Bullet>
          ))}
        </RowBullets>
        {media ? (
          <RowMedia
            initial={false}
            animate={done ? { opacity: 1, y: 0 } : { opacity: 0, y: 26 }}
            transition={{ duration: 0.6, ease: ease.out }}
          >
            <img src={media.src} alt={media.alt} loading="lazy" />
          </RowMedia>
        ) : null}
        {renderArtifact ? renderArtifact(phase) : null}
      </div>
      <ChipStack
        initial={false}
        animate={done ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
        transition={{ duration: 0.5, ease: ease.out }}
      >
        {card.project.stack.slice(0, 3).map((s) => (
          <Chip key={s} $bg={card.pastel}>
            {s}
          </Chip>
        ))}
      </ChipStack>
    </Row>
  );
};

/* --------------------------------------------------------- the artifact */

const nodeAnim = (visible: boolean, delay: number) => ({
  initial: false as const,
  animate: visible
    ? { opacity: 1, scale: 1, y: 0 }
    : { opacity: 0, scale: 0.8, y: 8 },
  transition: { ...pop, delay: visible ? delay : 0 },
});

const VistaArtifact: React.FC<{ phase: Phase }> = ({ phase }) => {
  const running = phase >= 1;
  const done = phase === 2;
  return (
    <Artifact>
      <ArtifactHead>
        <MonoLabel>Orchestrator · assembling</MonoLabel>
        <AnimatedIcon
          name="workflow"
          size={17}
          strokeWidth={1.8}
          active={running}
        />
      </ArtifactHead>
      <Diagram>
        <NodeColumn>
          {INTEGRATIONS.map((n, i) => (
            <NodeChip key={n} {...nodeAnim(running, 0.15 + i * 0.12)}>
              {n}
            </NodeChip>
          ))}
        </NodeColumn>
        <Hub {...nodeAnim(running, 0.8)}>
          Agentic
          <br />
          Orchestrator
          <HubSub>workflows · rules · skills</HubSub>
        </Hub>
        <NodeColumn>
          {OUTPUTS.map((n, i) => (
            <NodeChip key={n} {...nodeAnim(running, 1 + i * 0.12)}>
              {n}
            </NodeChip>
          ))}
        </NodeColumn>
      </Diagram>
      <ArtifactShot
        initial={false}
        animate={done ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
        transition={{ duration: 0.65, ease: ease.out }}
      >
        <img
          src={agenticOrchestrator}
          alt="Agentic orchestrator interface"
          loading="lazy"
        />
      </ArtifactShot>
    </Artifact>
  );
};

/* ------------------------------------------------------------ section */

const SeniorSection: React.FC = () => (
  <MissionWrap data-ink data-era={4}>
    <Inner>
      <TitleBlock>
        <Reveal>
          <TitleMeta>
            <MonoLabel $onInk>
              {SENIOR_SECTION.number} · Palo IT · {SENIOR_SECTION.years}
            </MonoLabel>
            <MonoLabel $onInk>Live run · 20 tickets · 5 engagements</MonoLabel>
          </TitleMeta>
          <DisplayTitle $size="display">
            Senior
            <br />
            Consultant era
          </DisplayTitle>
          <Statement>
            This era doesn't get listed — it gets executed.
            <StatementMuted>
              {" "}
              Keep scrolling: the orchestrator runs the board live, then
              compiles what shipped.
            </StatementMuted>
          </Statement>
        </Reveal>
      </TitleBlock>
    </Inner>
    <MissionBoard />
    <Inner>
      <RowsHead>
        <MonoLabel $onInk>Output · compiled engagements</MonoLabel>
        <AnimatedIcon
          name="activity"
          size={16}
          strokeWidth={2}
          color={color.paper}
        />
      </RowsHead>
      {SENIOR_SECTION.cards.map((card) => (
        <CompiledRow
          key={card.project.id}
          card={card}
          renderArtifact={
            card.project.id === "vista"
              ? (phase) => <VistaArtifact phase={phase} />
              : undefined
          }
        />
      ))}
    </Inner>
  </MissionWrap>
);

export default SeniorSection;
